/* eslint-disable clubmed/no-fake-component */
import React from 'react';
import moment from 'moment';
import { Card } from 'Components/frameworks/card/Card';
import { COLUMN_2 } from 'Components/frameworks/card/Card.constants';
import { formatWeekdayLong } from 'Components/frameworks/datepicker/Datepicker.utils';
import Image from 'Components/frameworks/image/Image';
import type { TabsItem } from 'Components/frameworks/tabs/Tabs';
import { ICONS } from 'Components/utils/icon/icons';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { buildProductArgs } from 'Utils/graphql';
import graphQLClient from 'Utils/graphql-client/graphqlClient';
import { LINE_QRCODE_PRODUCT_DRESSCODE } from 'Utils/request/requestDictionary';
import { API_DATE_FORMAT } from 'Utils/date/apiDate';
import { asImageProps } from 'Src/types/cast-helpers';
export type Dresscode = {
  image: string;
  label: string;
  startDateTime: string;
  weekdayName: string;
};
type ResortPlanning = Array<Dresscode>;
type GetTabsItemsProps = {
  associatedDresscodes: Array<ResortPlanning>;
  displayedDays: Array<moment$Moment>;
  dresscodeMissingText: string;
  dresscodeMissingTitle: string;
  language: string;
};

// Implementation carried on the component side to correct the problem of the timezone of the resort
const getDresscodesQuery = (productArgs, firstDate, lastDate) => `
query {
    resort(${productArgs}) {
      planning(type: "DRESSCODE", firstDate: "${firstDate}", lastDate: "${lastDate}") {
        image
        label
        startDateTime
        weekdayName
      }
    }
  }`;

export const getPlanning = (resortId: string, appContext: AppContext) => {
  const productArgs = buildProductArgs(resortId);
  const firstDate = moment().format(API_DATE_FORMAT);
  const lastDate = moment(firstDate, API_DATE_FORMAT).add(7, 'days').format(API_DATE_FORMAT);
  const response = graphQLClient.runQuery(
    getDresscodesQuery(productArgs, firstDate || '', lastDate || ''),
    appContext,
    {
      requestName: LINE_QRCODE_PRODUCT_DRESSCODE,
    },
  );
  return response;
};
export const getTabsItems = ({
  associatedDresscodes,
  displayedDays,
  dresscodeMissingText,
  dresscodeMissingTitle,
  language,
}: GetTabsItemsProps): Array<TabsItem> =>
  displayedDays.map((day, index) => {
    const dayName =
      associatedDresscodes[index].length > 0
        ? associatedDresscodes[index][0].weekdayName
        : formatWeekdayLong(day.day(), language);
    const content =
      associatedDresscodes[index].length > 0
        ? associatedDresscodes[index].map(({ image, label }) => (
            <div key={`dresscodeDay${index}`} className="flex h-full">
              <Image
                {...asImageProps({
                  className: 'w-1/2 overflow-hidden rounded-12',
                  classNameImg: 'overflow-hidden rounded-2',
                  imageUrl: image,
                  key: image,
                  groupKey: 'dresscode-images',
                  ratioKey: ratioUtils.R_38_BY_28,
                  ratioDisplay: 1 / 3,
                })}
              />
              <span className="p-20 text-16 font-bold leading-tight text-black">{label}</span>
            </div>
          ))
        : [
            <Card
              heading={{
                iconName: ICONS.DRESSCODE,
                title: dresscodeMissingTitle,
              }}
              key={`dresscodeDay${index}`}
              layout={COLUMN_2}
              text={{
                text: dresscodeMissingText,
              }}
            />,
          ];
    return {
      header: {
        title: (
          <div className="whitespace-nowrap capitalize" title={dayName}>
            {`${dayName} ${day.format('DD')}`}
          </div>
        ),
      },
      content,
      headerId: `resortPlanningDresscodeDay${index}Header`,
      panelId: `resortPlanningDresscodeDay${index}Panel`,
    };
  });
